import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from 'recharts';
import { getActuals, projectScenario } from '../../../services/scenarioService';
import CfoInsightsShell from '../CfoInsightsShell';
import {
  SCENARIO_BASE_GM_ASSUMPTION_PCT,
  formatScenarioGrossMarginPresentation,
  normalizeGrossMarginPct,
} from './scenarioGrossMargin';
import RequestBondJourney from './RequestBondJourney';
import { resolveOrgLabelsFromAccess } from '../orgLabels';
import {
  fingerprintScenarioInputs,
  formatScenarioCurrency,
  formatBareDollarAmountsInText,
  availableLocCapacity,
  explainCapitalRecommendation,
  buildRequestBondDecisionSummary,
  buildScenarioProjectContext,
  scenarioProjectContextIssue,
} from './scenarioModelPresentation';
import './ScenariosPage.css';
import api from '../../../services/api';

/* ==========================================================================
 * ScenariosPage
 *
 * All business data is sourced from the backend:
 *   GET  /api/v1/scenarios/actuals
 *   POST /api/v1/scenarios/{type}/project
 *
 * orgId is resolved server-side from the authenticated Firebase UID.
 *
 * If the actuals endpoint returns a pre-computed base (or other) scenario
 * projection, it is seeded into state on load so the default tab renders
 * without an additional round-trip.
 *
 * Input units (UI) -> contract units (request body) are converted at call
 * time: percent -> 0–1 fraction; pipelineAward "$M" -> USD dollars.
 * ==========================================================================*/

/* ── UI-only tab metadata (labels + chart colors; no business data) ─────── */

const SCENARIO_META = {
  base:   { label: 'Base case', color: '#1D9E75' },
  growth: { label: 'Growth',    color: '#3b82f6' },
  stress: { label: 'Stress',    color: '#ef4444' },
};
const SCENARIO_ORDER = ['base', 'growth', 'stress'];

const EMPTY_INPUTS = {
  winRate: '',
  grossMargin: '',
  payTermDays: '',
  retainagePct: '',
  pipelineAward: '',
};

/**
 * Required scenario default inputs (spec). Units match the Model Inputs
 * editor state: percent fields are percent-valued, pipelineAward is in $M.
 * Values are strings to keep the text inputs controlled (see notes on
 * EMPTY_INPUTS / deriveInputsFromActuals).
 *
 * buildAssumptionsPayload converts these to the backend contract units
 * at call time; the backend projection engine is unchanged.
 */
export const SCENARIO_DEFAULTS = {
  base: {
    winRate: '25',
    grossMargin: SCENARIO_BASE_GM_ASSUMPTION_PCT,
    payTermDays: '52',
    retainagePct: '10',
    pipelineAward: '2.8',
  },
  growth: {
    winRate: '35',
    grossMargin: '18.5',
    payTermDays: '45',
    retainagePct: '8',
    pipelineAward: '4.2',
  },
  stress: {
    winRate: '15',
    grossMargin: '11',
    payTermDays: '75',
    retainagePct: '12',
    pipelineAward: '1.2',
  },
};

/* ── Formatting helpers ─────────────────────────────────────────────────── */

function formatUsdMagnitude(dollars) {
  return formatScenarioCurrency(dollars, { style: 'compact' });
}

function formatThousandsKpi(kDollars) {
  // Values expressed in thousands (e.g., 180 -> "$180K", 1240 -> "$1.24M")
  if (kDollars === null || kDollars === undefined) return '—';
  const n = Number(kDollars);
  if (Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}M`;
  return `$${Math.round(n)}K`;
}

function formatMillionsKpi(mDollars) {
  if (mDollars === null || mDollars === undefined) return '—';
  const n = Number(mDollars);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(1)}M`;
}

/**
 * Anchored-actuals field formatter. Backend may return raw USD numbers or
 * pre-formatted display strings; we pass strings through and format numbers.
 */
function formatActualValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return formatUsdMagnitude(v);
  return String(v);
}

/** Backend may send 0–1, 0–100, a label string, or a small descriptor object. */
function formatModelConfidence(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'boolean') return null;
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    if (raw < 0) return null;
    if (raw <= 1) return `Model ${Math.round(raw * 100)}% confidence`;
    if (raw <= 100) return `Model ${Math.round(raw)}% confidence`;
    return null;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t.length ? t : null;
  }
  if (typeof raw === 'object') {
    const label = raw.label ?? raw.tier ?? raw.level ?? raw.name ?? raw.description;
    const score = raw.score ?? raw.confidence ?? raw.value ?? raw.rating;
    if (typeof label === 'string' && label.trim()) return label.trim();
    if (typeof score === 'number' && !Number.isNaN(score)) {
      if (score >= 0 && score <= 1) return `Model ${Math.round(score * 100)}% confidence`;
      if (score > 1 && score <= 100) return `Model ${Math.round(score)}% confidence`;
    }
    return null;
  }
  return null;
}

function normalizeDetailTableRows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (item == null) return null;
      if (typeof item === 'object' && !Array.isArray(item)) return item;
      if (typeof item === 'number' || typeof item === 'string' || typeof item === 'boolean') {
        return { value: item };
      }
      return null;
    })
    .filter(Boolean);
}

function humanizeDetailKey(k) {
  return String(k)
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function formatDetailCell(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') {
    if (Number.isNaN(v)) return '—';
    if (Math.abs(v) >= 1_000) return formatUsdMagnitude(v);
    return String(Math.round(v * 100) / 100);
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/* ── Backend -> view mapping ────────────────────────────────────────────── */

/**
 * Convert a backend ProjectionResponse into the shape the UI renders.
 * Returns null when no projection has been loaded for the given scenario —
 * callers render empty states in that case.
 */
function projectionToView(scenarioKey, projection) {
  if (!projection) return null;

  const backendKpis = projection.kpis || {};
  const workingCapGapDollars = numOrNull(backendKpis.workingCapitalGap);
  const revenueDollars = numOrNull(backendKpis.projectedRevenue12Mo);
  const retainageDollars = numOrNull(backendKpis.retainageLockup);

  const meta = SCENARIO_META[scenarioKey] || { label: scenarioKey, color: '#64748b' };

  return {
    label: meta.label,
    color: meta.color,
    // Backend reports raw dollars; normalize to thousands for the chart/KPI formatters.
    cashCurve: Array.isArray(projection.cashCurve)
      ? projection.cashCurve.map((v) => Number(v) / 1_000)
      : [],
    projectionMonths: Array.isArray(projection.projectionMonths) ? projection.projectionMonths : [],
    kpis: {
      dcoh: numOrNull(backendKpis.daysCashOnHand),
      currentRatio: numOrNull(backendKpis.currentRatio),
      grossMargin: numOrNull(backendKpis.grossMarginPct),
      workingCapGap: workingCapGapDollars != null ? workingCapGapDollars / 1_000 : null,
      revenue12: revenueDollars != null ? revenueDollars / 1_000_000 : null,
      retainageLockup: retainageDollars != null ? retainageDollars / 1_000 : null,
    },
    capital: Array.isArray(projection.capitalRecommendations)
      ? projection.capitalRecommendations.map((c) => ({
          type: c.facilityType || '—',
          amountRaw: numOrNull(c.recommendedAmount),
          amount: formatUsdMagnitude(c.recommendedAmount),
          note: c.rationale || '',
        }))
      : [],
    risks: Array.isArray(projection.riskFlags)
      ? projection.riskFlags.map((r) => ({
          level: normalizeRiskLevel(r.level),
          text: r.message || '',
        }))
      : [],
    partner: projection.partnerProfile
      ? {
          urgency: projection.partnerProfile.engagementUrgency || '',
          opportunity: projection.partnerProfile.lendingOpportunity || '',
          risk: projection.partnerProfile.underwritingRisk || '',
          pipeline: (projection.partnerProfile.pipelineProjects || '')
            .split(' | ').filter(Boolean).join(' · '),
        }
      : null,
    modelConfidenceLabel: formatModelConfidence(projection.modelConfidence),
    outflowScheduleRows: normalizeDetailTableRows(projection.outflowSchedule),
    arCollectionRows: normalizeDetailTableRows(projection.arCollections),
    addedProjectImpact: projection.addedProjectImpact || null,
    workingCapitalGapDollars: workingCapGapDollars,
    projectedCashLowDollars: numOrNull(backendKpis.projectedCashLow),
    availableLocHeadroomDollars: numOrNull(backendKpis.availableLocHeadroom),
  };
}

/** KPI delta for the working capital gap; "funding needed" only when the gap is positive. */
export function workingCapitalGapDelta(view) {
  const gap = view?.workingCapitalGapDollars;
  if (gap == null) return { text: '', className: 'sm2-delta-flat' };
  const low = view.projectedCashLowDollars;
  const headroom = view.availableLocHeadroomDollars;
  const basis = low != null && headroom != null
    ? ` · low ${formatUsdMagnitude(low)} + ${formatUsdMagnitude(headroom)} LOC headroom`
    : '';
  if (gap > 0) {
    return { text: `funding needed beyond cash and LOC headroom${basis}`, className: 'sm2-delta-warn' };
  }
  return { text: `No funding needed${basis}`, className: 'sm2-delta-up' };
}

function numOrNull(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function normalizeRiskLevel(level) {
  const l = (level || '').toLowerCase();
  if (l === 'red' || l === 'critical') return 'red';
  if (l === 'amber' || l === 'yellow' || l === 'warning') return 'amber';
  return 'green';
}

/**
 * Pull inputs (editor values) from a projection response. Prefers
 * `assumptionsUsed` (what the backend actually applied after any default
 * substitution) and falls back to `defaults` for forward-compat.
 *
 * Returns an object shaped like EMPTY_INPUTS (percent units for the four
 * percent fields, $M for pipelineAward) **only when at least one expected
 * field was successfully mapped**. Returns null otherwise — including when
 * `assumptionsUsed`/`defaults` is present but uses field names we don't
 * recognize — so callers guarded by `if (applied) setInputs(applied)` can
 * no-op instead of clobbering derived state with empty strings.
 */
function inputsFromProjection(projection) {
  const d = projection?.assumptionsUsed || projection?.defaults;
  if (!d) return null;
  // String-valued to keep the text inputs controlled (see notes in
  // deriveInputsFromActuals and EMPTY_INPUTS).
  const round1 = (n) => String(Math.round(Number(n) * 10) / 10);
  const out = { ...EMPTY_INPUTS };
  let mapped = 0;
  if (d.winRate != null) {
    out.winRate = round1(Number(d.winRate) * 100);
    mapped += 1;
  }
  if (d.grossMarginAssumption != null) {
    out.grossMargin = round1(Number(d.grossMarginAssumption) * 100);
    mapped += 1;
  }
  if (d.paymentTermsDays != null) {
    out.payTermDays = String(Math.round(Number(d.paymentTermsDays)));
    mapped += 1;
  }
  if (d.retainagePct != null) {
    out.retainagePct = round1(Number(d.retainagePct) * 100);
    mapped += 1;
  }
  if (d.pipelineAwardsValue != null) {
    out.pipelineAward = round1(Number(d.pipelineAwardsValue) / 1_000_000);
    mapped += 1;
  }
  return mapped > 0 ? out : null;
}

/**
 * Derive an initial inputs editor state from the raw actuals response.
 *
 * Used on mount so the first auto-projection is seeded with credible,
 * actuals-anchored assumptions (not zeros/NaN). Each field either maps to
 * a real actuals figure or falls back to an industry default:
 *
 *   winRate       → 35% (fallback; no actuals signal)
 *   grossMargin   → actuals.grossMarginPctYtd * 100, else 16%
 *   payTermDays   → 45 days (fallback; no actuals signal)
 *   retainagePct  → (accruedRetainage / activeWipValue) * 100, else 10%
 *   pipelineAward → avgPipelineProjectValue * wipJobCount / 1_000_000,
 *                   expressed in $M; else 2.5
 *
 * Returns the same shape as EMPTY_INPUTS, with numeric (not string) values
 * so buildAssumptionsPayload emits a clean payload on the first POST.
 */
function deriveInputsFromActuals(actuals) {
  const src = actuals && typeof actuals === 'object' ? actuals : {};

  const gmYtd = Number(src.grossMarginPctYtd);
  const grossMargin =
    Number.isFinite(gmYtd) && gmYtd > 0 ? gmYtd * 100 : 16;

  const retainageNum = Number(src.accruedRetainage);
  const wipDen = Number(src.activeWipValue);
  const retainagePct =
    Number.isFinite(retainageNum) &&
    Number.isFinite(wipDen) &&
    wipDen > 0
      ? (retainageNum / wipDen) * 100
      : 10;

  const avgProject = Number(src.avgPipelineProjectValue);
  const jobCount = Number(src.wipJobCount);
  const pipelineAward =
    Number.isFinite(avgProject) && Number.isFinite(jobCount)
      ? (avgProject * jobCount) / 1_000_000
      : 2.5;

  // Inputs state is always string-valued so text inputs stay controlled and
  // don't trigger React's "controlled → uncontrolled" warning when a user
  // clears a field.
  const round1 = (n) => String(Math.round(n * 10) / 10);

  return {
    winRate: '35',
    grossMargin: round1(grossMargin),
    payTermDays: '45',
    retainagePct: round1(retainagePct),
    pipelineAward: round1(pipelineAward),
  };
}

/**
 * Convert the UI input editor state into the ScenarioAssumptions shape the
 * backend expects. Single source of truth for the mount-time base projection
 * kickoff and the Recalculate button. Empty-string inputs coerce to 0 /
 * NaN; backend treats those as "use canonical defaults for this org".
 */
function buildAssumptionsPayload(src, addedProject) {
  return {
    winRate: Number(src.winRate) / 100,
    grossMarginAssumption: Number(src.grossMargin) / 100,
    paymentTermsDays: parseInt(src.payTermDays, 10),
    retainagePct: Number(src.retainagePct) / 100,
    pipelineAwardsValue: Number(src.pipelineAward) * 1_000_000,
    addedProject: buildAddedProjectPayload(addedProject),
  };
}

const EMPTY_ADDED_PROJECT = {
  projectName: '',
  projectType: '',
  contractValue: '',
  grossMarginEstimate: '',
  startDate: '',
  endDate: '',
  paymentTermsDays: '',
  apTimingDays: '',
  retainagePct: '',
  materialDepositAmount: '',
  bondingRequirement: '',
  capitalRequirement: '',
  confidencePct: '',
};

/**
 * Converts the Add-a-Project editor state into the request-scoped
 * AddedProjectRequest shape the backend expects, or null when the project
 * isn't enabled / lacks the minimum required fields (name + contract value).
 */
function buildAddedProjectPayload(p) {
  if (!p || !p.projectName || !p.contractValue || !p.startDate) return null;
  return {
    projectName: p.projectName,
    projectType: p.projectType || null,
    contractValue: Number(p.contractValue),
    grossMarginEstimate: Number(p.grossMarginEstimate || 0) / 100,
    startDate: p.startDate,
    endDate: p.endDate || null,
    durationMonths: p.endDate ? null : (p.durationMonths ? parseInt(p.durationMonths, 10) : null),
    paymentTermsDays: parseInt(p.paymentTermsDays, 10) || 45,
    apTimingDays: parseInt(p.apTimingDays, 10) || 30,
    retainagePct: Number(p.retainagePct || 0) / 100,
    materialDepositAmount: p.materialDepositAmount ? Number(p.materialDepositAmount) : null,
    bondingRequirement: p.bondingRequirement ? Number(p.bondingRequirement) : null,
    capitalRequirement: p.capitalRequirement ? Number(p.capitalRequirement) : null,
    confidencePct: p.confidencePct ? Number(p.confidencePct) / 100 : null,
  };
}

/**
 * Normalize the actuals endpoint response into the four shapes the page
 * needs: an actuals record for the Anchored-to-Actuals card, and any
 * pre-computed projections keyed by scenario so we can avoid a second call.
 */
function normalizeActualsResponse(resp) {
  if (!resp || typeof resp !== 'object') {
    return { actuals: null, projections: {} };
  }

  // Coalesce alternative backend key casings so we tolerate every actuals
  // payload shape we've seen: legacy camelCase (activeWip, retainageHeld),
  // acronym-preserving (activeWIP), and the current backend contract
  // (activeWipValue, accruedRetainage).
  //
  // LOC facility is composed from three separate fields when the backend
  // doesn't provide a pre-formatted `locFacility` string: prefer
  // locFacilitySize (the committed line), fall back to locBalanceYearEnd
  // (the drawn balance) when size isn't surfaced. Rate is rendered to one
  // decimal. When no rate is present we can't render a credible string, so
  // the card shows the standard em-dash fallback.
  const fmtInt = (n) =>
    Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  // Guard explicitly against null/undefined before calling Number() - `Number(null) === 0`
  // would otherwise turn a genuinely missing rate/size into a fabricated "$0 @ 0.0%".
  const rate = resp.locInterestRate != null ? Number(resp.locInterestRate) : null;
  const locSize = resp.locFacilitySize != null ? Number(resp.locFacilitySize) : null;
  const locBal = resp.locBalanceYearEnd != null ? Number(resp.locBalanceYearEnd) : null;
  let locFacility = resp.locFacility ?? null;
  if (locFacility == null && rate != null && Number.isFinite(rate)) {
    if (locSize != null && Number.isFinite(locSize)) {
      locFacility = `$${fmtInt(locSize)} @ ${(rate * 100).toFixed(1)}%`;
    } else if (locBal != null && Number.isFinite(locBal)) {
      locFacility = `$${fmtInt(locBal)} @ ${(rate * 100).toFixed(1)}%`;
    }
  }

  const actuals = {
    startingCash: resp.startingCash,
    activeWip: resp.activeWip ?? resp.activeWIP ?? resp.activeWipValue,
    activeWipSource: resp.activeWipValueSource ?? null,
    netUnderbilling: resp.netUnderbilling,
    netUnderbillingSource: resp.netUnderbillingSource ?? null,
    locFacility,
    locFacilitySize: locSize != null && Number.isFinite(locSize) ? locSize : null,
    locBalanceYearEnd: locBal != null && Number.isFinite(locBal) ? locBal : null,
    locFacilitySource: resp.locFacilitySource ?? null,
    retainageHeld: resp.retainageHeld ?? resp.accruedRetainage,
    retainageSource: resp.retainageSource ?? null,
    asOfDate: resp.asOfDate,
    grossMarginPctYtd: normalizeGrossMarginPct(
      resp.grossMarginPctYtd ?? resp.grossMarginPercent ?? resp.grossMargin
    ),
  };

  const projections = {};
  if (resp.scenarios && typeof resp.scenarios === 'object') {
    for (const key of SCENARIO_ORDER) {
      if (resp.scenarios[key]) projections[key] = resp.scenarios[key];
    }
  }
  // Some backends expose the base projection at the top level instead.
  if (!projections.base && resp.baseScenario) {
    projections.base = resp.baseScenario;
  }

  return { actuals, projections };
}

/* ── Error copy ─────────────────────────────────────────────────────────── */

const ERROR_COPY = {
  400: { title: 'Invalid assumptions', message: 'The request was rejected. Check the assumption values and try again.' },
  401: { title: 'Session expired', message: 'Signing you out…' },
  403: { title: 'Access denied', message: "You don't have permission to run scenarios for this organization." },
  404: { title: 'Scenario not found', message: 'The scenario or organization could not be found. If this persists, contact support.' },
  500: { title: 'Scenario engine error', message: 'Something went wrong on our side. Please try again.' },
};
function errorCopyFor(status) {
  return ERROR_COPY[status] || {
    title: 'Unexpected error',
    message: 'The scenario could not be run. Please try again, and contact support if this persists.',
  };
}

/* ── Component ──────────────────────────────────────────────────────────── */

export default function ScenariosPage() {
  const navigate = useNavigate();
  const [orgAccess, setOrgAccess] = useState(null);
  const { displayName: ORG_DISPLAY_NAME, shortName: ORG_SHORT_NAME } =
    resolveOrgLabelsFromAccess(orgAccess);

  const [activeScenario, setActiveScenario] = useState('base');
  const [inputs, setInputs] = useState(() => ({ ...EMPTY_INPUTS }));
  const [addedProject, setAddedProject] = useState(() => ({ ...EMPTY_ADDED_PROJECT }));
  const [addedProjectEnabled, setAddedProjectEnabled] = useState(false);
  // projections is keyed by scenarioKey; stores backend ProjectionResponse
  // once a run has succeeded or once getActuals returns one inline.
  const [projections, setProjections] = useState({});
  const [actuals, setActuals] = useState(null);
  const [actualsStatus, setActualsStatus] = useState('idle'); // idle | loading | success | error
  const [runStatus, setRunStatus] = useState('idle'); // idle | loading | success | error
  const [errorStatus, setErrorStatus] = useState(null);
  const [btnLabel, setBtnLabel] = useState('Recalculate Projections');
  const [btnUpdated, setBtnUpdated] = useState(false);
  // Tracks the specific Bar the cursor is over so the KPI chart tooltip
  // can render only that bar's value instead of the full group.
  const [activeBarKey, setActiveBarKey] = useState(null);
  // Fingerprint of inputs that produced the last successful active-scenario
  // projection. When current inputs diverge, results are stale and Request
  // Bond stays disabled until recalculation succeeds.
  const [lastCalculatedFingerprint, setLastCalculatedFingerprint] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/auth/organization-access');
        if (!cancelled) setOrgAccess(resp.data || null);
      } catch {
        if (!cancelled) setOrgAccess(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load actuals on mount so the Anchored-to-Actuals card populates.
  // The scenario-switch effect owns input defaults and auto-projection.
  useEffect(() => {
    let cancelled = false;

    setActualsStatus('loading');

    (async () => {
      try {
        const resp = await getActuals();
        if (cancelled) return;

        const { actuals: nextActuals } = normalizeActualsResponse(resp);
        setActuals(nextActuals);
        setActualsStatus('success');
      } catch {
        if (cancelled) return;
        setActualsStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Latest added-project state, readable inside the tab-switch effect below
  // without adding it to that effect's dependency array (which would reset
  // the whole tab - inputs, cached projection, loading state - on every
  // keystroke in the Add a Project form). Without this, switching tabs
  // silently dropped the added project from Capital Recommendations, Risk
  // Flags, and the Partner Conversation Profile even when "Model this
  // project's impact" was still checked.
  const addedProjectRef = React.useRef({ enabled: addedProjectEnabled, project: addedProject });
  useEffect(() => {
    addedProjectRef.current = { enabled: addedProjectEnabled, project: addedProject };
  }, [addedProjectEnabled, addedProject]);

  // Switching scenarios (including the initial mount with activeScenario
  // = 'base') loads that scenario's required defaults from spec into the
  // inputs editor and immediately fires a projection with those defaults
  // plus the current added project (if enabled). The returned projection is
  // stored under the scenario key so the KPI row, cash curve, comparison
  // chart, capital recommendations, risk flags, and partner profile reflect
  // the canonical baseline (and any added project) for the selected
  // scenario without requiring a Recalculate click.
  //
  // Inputs remain editable after load; Recalculate uses the CURRENT inputs
  // state. If the backend echoes a recognizable `assumptionsUsed`, we
  // reflect server-applied values back into the editor; otherwise we keep
  // the frontend defaults intact (inputsFromProjection returns null for
  // unrecognized shapes).
  useEffect(() => {
    let cancelled = false;

    const defaults = SCENARIO_DEFAULTS[activeScenario] || { ...EMPTY_INPUTS };
    setInputs({ ...defaults });
    setErrorStatus(null);
    // Drop any cached projection for this tab so we never show another run's
    // KPIs/curve while the new POST is in flight (demo-breaking stale state).
    setProjections((prev) => {
      if (!prev[activeScenario]) return prev;
      const next = { ...prev };
      delete next[activeScenario];
      return next;
    });
    setLastCalculatedFingerprint(null);
    setRunStatus('loading');

    (async () => {
      try {
        const { enabled, project } = addedProjectRef.current;
        const result = await projectScenario(
          activeScenario,
          buildAssumptionsPayload(defaults, enabled ? project : null)
        );
        if (cancelled) return;
        setProjections((prev) => ({ ...prev, [activeScenario]: result }));
        const applied = inputsFromProjection(result);
        const nextInputs = applied || defaults;
        if (applied) setInputs(applied);
        setLastCalculatedFingerprint(
          fingerprintScenarioInputs(nextInputs, enabled, project)
        );
        setRunStatus('success');
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status ?? 0;
        setErrorStatus(code);
        setRunStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenario]);

  // The KPI Comparison by Scenario chart needs all three scenarios loaded to
  // be a comparison at all - without this, it only shows bars for whichever
  // tabs the user has happened to visit (1 bar on first load, 2 after
  // visiting Growth, etc). Backfill the two non-active scenarios in the
  // background on mount using their canonical defaults, whenever the added
  // project (if any), so the chart is populated immediately.
  const refreshOtherScenarios = React.useCallback((skipKey, addedProjectPayload) => {
    SCENARIO_ORDER.filter((key) => key !== skipKey).forEach((key) => {
      const defaults = SCENARIO_DEFAULTS[key] || { ...EMPTY_INPUTS };
      projectScenario(key, buildAssumptionsPayload(defaults, addedProjectPayload))
        .then((result) => {
          setProjections((prev) => ({ ...prev, [key]: result }));
        })
        .catch(() => {
          // Best-effort backfill for the comparison chart; the active tab's
          // own error handling already surfaces failures to the user.
        });
    });
  }, []);

  useEffect(() => {
    refreshOtherScenarios('base', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // View-model for the active scenario. null until a projection is loaded.
  const view = useMemo(
    () => projectionToView(activeScenario, projections[activeScenario]),
    [activeScenario, projections]
  );
  const gmPresentation = formatScenarioGrossMarginPresentation(actuals?.grossMarginPctYtd);

  const baseView = useMemo(
    () => projectionToView('base', projections.base),
    [projections]
  );

  // Comparison chart: metric-first grouping (XAxis = metric, bars = scenarios).
  // All values come from live projections only; chart is empty until each
  // scenario has been projected at least once.
  //
  // Days Cash on Hand and Current Ratio are intentionally excluded here: the
  // projection engine computes both from static point-in-time actuals only
  // (balance-sheet snapshot values), not from the scenario-specific cash
  // curve or the added project - so they are identical across Base/Growth/
  // Stress by design and would misleadingly render as flat, identical bars
  // in a "by scenario" comparison. They remain visible in the KPI cards
  // above, labeled "Baseline" there. Working capital gap, margin, revenue,
  // and retainage lockup are all genuinely scenario-sensitive (they vary
  // with each scenario's win rate / margin / retainage assumptions) and are
  // scaled here purely for chart readability, same convention as the
  // existing "×10" label below.
  const comparisonData = useMemo(() => {
    const kpisFor = (key) => {
      const v = projectionToView(key, projections[key]);
      return {
        wcGap100k:  v?.kpis.workingCapGap != null ? Number((v.kpis.workingCapGap / 100).toFixed(2)) : 0,
        margin:     v?.kpis.grossMargin  ?? 0,
        revenue:    v?.kpis.revenue12    ?? 0,
        retainage10k: v?.kpis.retainageLockup != null ? Number((v.kpis.retainageLockup / 10).toFixed(2)) : 0,
      };
    };
    const b = kpisFor('base');
    const g = kpisFor('growth');
    const s = kpisFor('stress');
    return [
      { metric: 'WC gap ($100K)',      base: b.wcGap100k,   growth: g.wcGap100k,   stress: s.wcGap100k },
      { metric: 'Margin %',            base: b.margin,      growth: g.margin,      stress: s.margin },
      { metric: 'Revenue $M',          base: b.revenue,     growth: g.revenue,     stress: s.revenue },
      { metric: 'Retainage ($10K)',    base: b.retainage10k, growth: g.retainage10k, stress: s.retainage10k },
    ];
  }, [projections]);

  const cashCurveData = useMemo(() => {
    if (!view) return [];
    const months = view.projectionMonths || [];
    return (view.cashCurve || []).map((value, i) => ({
      month: months[i] || `M${i + 1}`,
      value: Number(value),
    }));
  }, [view]);

  const activeMeta = SCENARIO_META[activeScenario];

  // Reset the Model Inputs editor to the active scenario's required
  // defaults (spec) and immediately re-run the projection so the UI
  // reflects those defaults end-to-end (KPI row, cash curve, comparison
  // chart, capital recs, risk flags).
  const resetInputs = async () => {
    if (runStatus === 'loading') return;
    const defaults = SCENARIO_DEFAULTS[activeScenario] || { ...EMPTY_INPUTS };
    setInputs({ ...defaults });
    setRunStatus('loading');
    setErrorStatus(null);
    try {
      const result = await projectScenario(
        activeScenario,
        buildAssumptionsPayload(defaults, addedProjectEnabled ? addedProject : null)
      );
      setProjections((prev) => ({ ...prev, [activeScenario]: result }));
      const applied = inputsFromProjection(result);
      const nextInputs = applied || defaults;
      if (applied) setInputs(applied);
      setLastCalculatedFingerprint(
        fingerprintScenarioInputs(nextInputs, addedProjectEnabled, addedProject)
      );
      setRunStatus('success');
    } catch (err) {
      const code = err?.response?.status ?? 0;
      setErrorStatus(code);
      setRunStatus('error');
    }
  };

  const handleRun = async () => {
    if (runStatus === 'loading') return;
    setRunStatus('loading');
    setErrorStatus(null);
    try {
      const addedProjectPayload = addedProjectEnabled ? addedProject : null;
      const payload = buildAssumptionsPayload(inputs, addedProjectPayload);
      const result = await projectScenario(activeScenario, payload);
      setProjections((prev) => ({ ...prev, [activeScenario]: result }));
      // Keep the KPI Comparison by Scenario chart in sync with the added
      // project: the other two scenarios use their own canonical defaults
      // (this page doesn't track per-tab custom inputs) plus the same
      // added project, so the comparison reflects its impact everywhere.
      refreshOtherScenarios(activeScenario, addedProjectPayload);
      // Echo the server-applied assumptions back into the editor so any
      // backend default substitutions (for blank inputs) become visible.
      const appliedInputs = inputsFromProjection(result);
      const nextInputs = appliedInputs || inputs;
      if (appliedInputs) setInputs(appliedInputs);
      setLastCalculatedFingerprint(
        fingerprintScenarioInputs(nextInputs, addedProjectEnabled, addedProject)
      );
      setRunStatus('success');
      setBtnLabel('Updated ✓');
      setBtnUpdated(true);
      window.setTimeout(() => {
        setBtnLabel('Recalculate Projections');
        setBtnUpdated(false);
      }, 1200);
    } catch (err) {
      const code = err?.response?.status ?? 0;
      setErrorStatus(code);
      setRunStatus('error');
    }
  };

  const setInput = (key) => (e) => {
    setInputs((prev) => ({ ...prev, [key]: e.target.value }));
  };
  // Normalize on blur: strip suffix characters and coerce back to a string
  // representation so state stays string-valued (see deriveInputsFromActuals).
  const parseInputOnBlur = (key, kind) => (e) => {
    const raw = e.target.value;
    if (kind === 'int') {
      const n = parseInt(String(raw).replace(/[^\d-]/g, ''), 10);
      setInputs((prev) => ({
        ...prev,
        [key]: Number.isNaN(n) ? prev[key] : String(n),
      }));
    } else {
      const n = parseFloat(String(raw).replace(/[^\d.-]/g, ''));
      setInputs((prev) => ({
        ...prev,
        [key]: Number.isNaN(n) ? prev[key] : String(n),
      }));
    }
  };

  // Baselines for delta text come from the live base projection when we
  // have one; otherwise deltas render as a neutral "—" against self.
  const baseKpis = baseView?.kpis;

  const hasProjectionDetails =
    (view?.outflowScheduleRows?.length ?? 0) > 0 ||
    (view?.arCollectionRows?.length ?? 0) > 0;

  const currentInputFingerprint = useMemo(
    () => fingerprintScenarioInputs(inputs, addedProjectEnabled, addedProject),
    [inputs, addedProjectEnabled, addedProject]
  );
  const resultsStale =
    Boolean(view) &&
    runStatus === 'success' &&
    lastCalculatedFingerprint != null &&
    currentInputFingerprint !== lastCalculatedFingerprint;
  const projectContextIssue = scenarioProjectContextIssue({
    enabled: addedProjectEnabled,
    project: addedProject,
  });
  const projectContext = useMemo(
    () => buildScenarioProjectContext({
      enabled: addedProjectEnabled,
      project: addedProject,
      scenarioKey: activeScenario,
    }),
    [addedProjectEnabled, addedProject, activeScenario]
  );
  const requestBondBlocked =
    !view || runStatus !== 'success' || resultsStale || lastCalculatedFingerprint == null
    || projectContext == null;

  const locFacilitySize =
    actuals?.locFacilitySize != null && Number.isFinite(Number(actuals.locFacilitySize))
      ? Number(actuals.locFacilitySize)
      : null;
  const locDrawnBalance =
    actuals?.locBalanceYearEnd != null && Number.isFinite(Number(actuals.locBalanceYearEnd))
      ? Number(actuals.locBalanceYearEnd)
      : null;
  const availableLocHeadroom =
    view?.availableLocHeadroomDollars ?? availableLocCapacity(locFacilitySize, locDrawnBalance);
  const gapDelta = workingCapitalGapDelta(view);

  const primaryCapital =
    view?.capital?.find((c) => c.type === 'Revolving LOC') || view?.capital?.[0] || null;
  const illustrativeCapital =
    view?.capital?.find((c) => c.type === 'Illustrative LOC step-up') || null;
  const capitalExplanation = useMemo(
    () =>
      explainCapitalRecommendation({
        locFacilitySize,
        locDrawnBalance,
        availableLocHeadroom: view?.availableLocHeadroomDollars ?? null,
        workingCapitalGap: view?.workingCapitalGapDollars ?? null,
        recommendedAmount: primaryCapital?.amountRaw ?? null,
        illustrativeStepUpAmount: illustrativeCapital?.amountRaw ?? null,
        facilityType: primaryCapital?.type,
        rationale: primaryCapital?.note,
      }),
    [
      locFacilitySize,
      locDrawnBalance,
      view?.availableLocHeadroomDollars,
      view?.workingCapitalGapDollars,
      primaryCapital,
      illustrativeCapital,
    ]
  );

  const primaryRisk =
    view?.risks?.find((r) => r.level === 'red')?.text
    || view?.risks?.find((r) => r.level === 'amber')?.text
    || null;

  const decisionSummary = useMemo(
    () =>
      buildRequestBondDecisionSummary({
        projectName:
          addedProjectEnabled && addedProject?.projectName
            ? addedProject.projectName
            : null,
        contractValue: addedProjectEnabled ? addedProject?.contractValue : null,
        bondingRequirement: addedProjectEnabled ? addedProject?.bondingRequirement : null,
        workingCapitalImpact: view?.addedProjectImpact?.workingCapitalImpact?.value,
        capitalNeed: view?.addedProjectImpact?.capitalNeed?.value,
        currentLoc: locFacilitySize,
        locDrawnBalance,
        capitalExplanation,
        riskState: primaryRisk,
      }),
    [
      addedProjectEnabled,
      addedProject,
      view?.addedProjectImpact,
      locFacilitySize,
      locDrawnBalance,
      capitalExplanation,
      primaryRisk,
    ]
  );

  return (
    <CfoInsightsShell
      orgDisplayName={ORG_DISPLAY_NAME}
      orgShortName={ORG_SHORT_NAME}
      activeTabId={null}
      onTabClick={(id) => navigate(`/cfo-insights?tab=${id}`)}
      fullWidth={true}
    >
      <div className="sm2">
        {/* ── PAGE HEADER + SCENARIO TABS ─────────────────────────── */}
        <div className="sm2-page-header">
          <div>
            <div className="sm2-page-title-row">
              <h1 className="sm2-page-title">Scenario Modeling</h1>
              {view?.modelConfidenceLabel ? (
                <span className="sm2-confidence-badge" title="Model confidence">
                  {view.modelConfidenceLabel}
                </span>
              ) : null}
            </div>
            <div className="sm2-page-desc">
              Forward cash profile modeled from live WIP, balance sheet, and income statement data. Edit assumptions to stress-test outcomes.
            </div>
          </div>
          <div className="sm2-scenario-tabs" role="tablist" aria-label="Scenario">
            {SCENARIO_ORDER.map((key) => {
              const isActive = activeScenario === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`sm2-stab${isActive ? ` active-${key}` : ''}`}
                  disabled={runStatus === 'loading'}
                  onClick={() => setActiveScenario(key)}
                >
                  {SCENARIO_META[key].label}
                </button>
              );
            })}
          </div>
        </div>

        {actualsStatus === 'error' && (
          <div className="sm2-inline-error sm2-inline-error-amber">
            <div className="sm2-inline-error-title">Couldn't load current actuals</div>
            <div>
              We couldn't reach the scenarios service. You can still edit assumptions and recalculate; anchored values will populate once the service responds.
            </div>
          </div>
        )}

        {/* ── KPI ROW ─────────────────────────────────────────────── */}
        <div className="sm2-metrics-row">
          <KpiCard
            label="Days cash on hand (Baseline)"
            value={formatDcoh(view?.kpis.dcoh)}
            signal={signalFor(view?.kpis.dcoh, [35, 20], 'higher')}
            delta="Current-state actuals · not scenario-adjusted"
            deltaClass="sm2-delta-flat"
          />
          <KpiCard
            label="Current ratio (Baseline)"
            value={view?.kpis.currentRatio != null ? `${view.kpis.currentRatio.toFixed(2)}×` : '—'}
            signal={signalFor(view?.kpis.currentRatio, [1.5, 1.25], 'higher')}
            delta="Current-state actuals · not scenario-adjusted"
            deltaClass="sm2-delta-flat"
          />
          <KpiCard
            label={gmPresentation.kpiLabel}
            value={view?.kpis.grossMargin != null ? `${view.kpis.grossMargin.toFixed(1)}%` : '—'}
            signal={signalFor(view?.kpis.grossMargin, [15, 12], 'higher')}
            delta={gmPresentation.kpiNote}
            deltaClass="sm2-delta-flat"
          />
          <KpiCard
            label="Working capital gap"
            value={formatThousandsKpi(view?.kpis.workingCapGap)}
            signal={signalFor(view ? -view.kpis.workingCapGap : null, [-200, -400], 'higher')}
            delta={gapDelta.text}
            deltaClass={gapDelta.className}
          />
          <KpiCard
            label="Revenue (12-mo proj.)"
            value={formatMillionsKpi(view?.kpis.revenue12)}
            signal="sm2-signal-green"
            delta={deltaText(view?.kpis.revenue12, baseKpis?.revenue12, { unit: 'M', prefix: '$', decimals: 1 })}
            deltaClass={deltaClass(view?.kpis.revenue12, baseKpis?.revenue12)}
          />
        </div>

        {/* ── CHARTS ROW ──────────────────────────────────────────── */}
        <div className="sm2-charts-row">
          <div className="sm2-chart-card">
            <div className="sm2-chart-label">
              Projected Cash Balance — {view?.label || activeMeta.label} · before LOC draws or new funding
            </div>
            <div className="sm2-chart-wrap">
              {cashCurveData.length === 0 ? (
                <div className="sm2-chart-empty">
                  {runStatus === 'loading'
                    ? 'Running projection…'
                    : 'Recalculate to populate the cash curve for this scenario.'}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={cashCurveData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sm2CashFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={view.color} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={view.color} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(128,128,128,0.08)" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: '#576a8a', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: '#576a8a', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(1)}M`}
                      domain={[
                        (dataMin) => Math.min(0, Math.floor(dataMin * 1.1)),
                        (dataMax) => Math.ceil(Math.max(0, dataMax) * 1.1),
                      ]}
                    />
                    <Tooltip
                      formatter={(v) => `$${(Number(v) / 1000).toFixed(2)}M`}
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        color: '#e2e8f0',
                      }}
                      labelStyle={{ color: '#94a3b8' }}
                      itemStyle={{ color: '#e2e8f0' }}
                      cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={view.color}
                      strokeWidth={2}
                      fill="url(#sm2CashFill)"
                      dot={{ r: 3, fill: view.color, stroke: view.color }}
                      activeDot={{ r: 5 }}
                      name={view.label}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="sm2-chart-card">
            <div className="sm2-chart-label">KPI Comparison by Scenario</div>
            <div className="sm2-chart-wrap">
              {comparisonData.length === 0 ? (
                <div className="sm2-chart-empty">
                  Recalculate scenarios to compare their KPIs.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData} barSize={16} barGap={2} barCategoryGap="20%" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(128,128,128,0.08)" vertical={false} />
                    <XAxis
                      dataKey="metric"
                      tick={{ fill: '#576a8a', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: '#576a8a', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length || !activeBarKey) return null;
                        const entry = payload.find((p) => p.dataKey === activeBarKey);
                        if (!entry) return null;
                        return (
                          <div style={{
                            backgroundColor: '#1e293b',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                          }}>
                            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
                            <div style={{ color: entry.color, fontWeight: 500 }}>
                              {entry.name}: {Number(entry.value).toFixed(2).replace(/\.00$/, '')}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="base"   name="Base"   fill="#1D9E75" radius={[2, 2, 0, 0]}
                      onMouseEnter={() => setActiveBarKey('base')}
                      onMouseLeave={() => setActiveBarKey(null)} />
                    <Bar dataKey="growth" name="Growth" fill="#3b82f6" radius={[2, 2, 0, 0]}
                      onMouseEnter={() => setActiveBarKey('growth')}
                      onMouseLeave={() => setActiveBarKey(null)} />
                    <Bar dataKey="stress" name="Stress" fill="#ef4444" radius={[2, 2, 0, 0]}
                      onMouseEnter={() => setActiveBarKey('stress')}
                      onMouseLeave={() => setActiveBarKey(null)} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* ── MODEL INPUTS + ANCHORED ACTUALS ─────────────────────── */}
        <div className="sm2-inputs-row">
          <div className="sm2-card">
            <div className="sm2-card-header">
              <span className="sm2-card-title">Model Inputs</span>
              <button
                type="button"
                className="sm2-card-action"
                onClick={resetInputs}
                disabled={runStatus === 'loading'}
              >
                Reset to defaults
              </button>
            </div>
            <div className="sm2-assump-rows">
              <AssumpRow
                label="Win rate"
                value={inputs.winRate ?? ''}
                baseline=""
                onChange={setInput('winRate')}
                onBlur={parseInputOnBlur('winRate', 'float')}
                disabled={runStatus === 'loading'}
              />
              <AssumpRow
                label={gmPresentation.inputLabel}
                value={inputs.grossMargin ?? ''}
                baseline={gmPresentation.actualDisplay}
                onChange={setInput('grossMargin')}
                onBlur={parseInputOnBlur('grossMargin', 'float')}
                disabled={runStatus === 'loading'}
              />
              <AssumpRow
                label="Avg payment terms"
                value={inputs.payTermDays ?? ''}
                baseline=""
                onChange={setInput('payTermDays')}
                onBlur={parseInputOnBlur('payTermDays', 'int')}
                disabled={runStatus === 'loading'}
              />
              <AssumpRow
                label="Retainage held"
                value={inputs.retainagePct ?? ''}
                baseline=""
                onChange={setInput('retainagePct')}
                onBlur={parseInputOnBlur('retainagePct', 'float')}
                disabled={runStatus === 'loading'}
              />
              <AssumpRow
                label="Pipeline awards (12-mo)"
                value={inputs.pipelineAward ?? ''}
                baseline=""
                onChange={setInput('pipelineAward')}
                onBlur={parseInputOnBlur('pipelineAward', 'float')}
                disabled={runStatus === 'loading'}
              />
            </div>
          </div>
          <div className="sm2-card">
            <div className="sm2-card-header">
              <span className="sm2-card-title">Anchored to Actuals</span>
              <span className="sm2-card-meta">
                {actualsStatus === 'loading' ? 'Loading…' : 'Read-only · source-backed'}
              </span>
            </div>
            <div>
              <ActualRow
                k={actuals?.asOfDate ? `Starting cash (${actuals.asOfDate})` : 'Starting cash'}
                v={formatActualValue(actuals?.startingCash)}
              />
              <ActualRow
                k="Active WIP value"
                v={actuals?.activeWipSource === 'missing_data' ? 'Missing data' : formatActualValue(actuals?.activeWip)}
                source={actuals?.activeWipSource}
              />
              <ActualRow
                k="Net underbilling"
                v={actuals?.netUnderbillingSource === 'missing_data' ? 'Missing data' : formatActualValue(actuals?.netUnderbilling)}
                source={actuals?.netUnderbillingSource}
              />
              <ActualRow
                k="Committed LOC facility"
                v={actuals?.locFacilitySource === 'missing_data' ? 'Missing data' : formatActualValue(actuals?.locFacility)}
                source={actuals?.locFacilitySource}
              />
              {actuals?.locBalanceYearEnd != null ? (
                <ActualRow
                  k="LOC drawn / utilized"
                  v={formatScenarioCurrency(actuals.locBalanceYearEnd, { style: 'full' })}
                  source={actuals?.locFacilitySource}
                />
              ) : null}
              {availableLocHeadroom != null ? (
                <ActualRow
                  k="Available LOC headroom"
                  v={formatScenarioCurrency(availableLocHeadroom, { style: 'full' })}
                  source="constructpay_projection"
                />
              ) : null}
              <ActualRow
                k="Current actual gross margin"
                v={
                  actuals?.grossMarginPctYtd != null
                    ? `${Number(actuals.grossMarginPctYtd).toFixed(2)}%`
                    : '—'
                }
              />
              <ActualRow
                k="Retainage held"
                v={actuals?.retainageSource === 'missing_data' ? 'Missing data' : formatActualValue(actuals?.retainageHeld)}
                source={actuals?.retainageSource}
              />
            </div>
          </div>
        </div>

        {/* ── ADD A PROJECT ───────────────────────────────────────── */}
        <AddProjectPanel
          project={addedProject}
          onChange={setAddedProject}
          enabled={addedProjectEnabled}
          onToggle={setAddedProjectEnabled}
          disabled={runStatus === 'loading'}
        />

        {addedProjectEnabled && view?.addedProjectImpact ? (
          <AddedProjectImpactCard impact={view.addedProjectImpact} />
        ) : null}

        {/* ── RUN BAR ─────────────────────────────────────────────── */}
        <div className="sm2-run-bar">
          <button
            type="button"
            className={`sm2-btn sm2-btn-primary${btnUpdated ? ' sm2-btn-updated' : ''}`}
            disabled={runStatus === 'loading'}
            onClick={handleRun}
            data-testid="scenario-recalculate"
          >
            {runStatus === 'loading' ? 'Running…' : btnLabel}
          </button>
          {resultsStale ? (
            <div
              className="sm2-inline-error sm2-inline-error-amber"
              data-testid="scenario-stale-banner"
              style={{ marginLeft: 12, flex: 1 }}
            >
              <div className="sm2-inline-error-title">Recalculation required</div>
              <div>
                Scenario inputs changed since the last successful run. Results below are stale.
                Recalculate before requesting a bond.
              </div>
            </div>
          ) : null}
        </div>

        {/* ── ERROR STATE ─────────────────────────────────────────── */}
        {runStatus === 'error' && (
          <div
            className={`sm2-inline-error ${
              errorStatus === 403 ? 'sm2-inline-error-amber' : 'sm2-inline-error-red'
            }`}
          >
            <div className="sm2-inline-error-title">{errorCopyFor(errorStatus).title}</div>
            <div>{errorCopyFor(errorStatus).message}</div>
          </div>
        )}

        {/* ── CAPITAL RECOMMENDATIONS ─────────────────────────────── */}
        <div>
          <div className="sm2-card-header" style={{ marginBottom: 12 }}>
            <span className="sm2-card-title">Capital Recommendations — {view?.label || activeMeta.label}</span>
          </div>
          <div className="sm2-cap-grid">
            {view?.capital?.length ? (
              view.capital.map((c, i) => {
                const isPrimary = c.type === 'Revolving LOC' || (i === 0 && c.type !== 'Illustrative LOC step-up');
                const isIllustrative = c.type === 'Illustrative LOC step-up';
                return (
                  <div
                    key={`${c.type}-${i}`}
                    className="sm2-cap-card"
                    data-testid={isPrimary ? 'capital-rec-primary' : isIllustrative ? 'capital-rec-illustrative' : undefined}
                  >
                    <div className="sm2-cap-type">
                      {isPrimary
                        ? capitalExplanation.label
                        : isIllustrative
                          ? 'Illustrative 10% LOC scenario'
                          : c.type}
                    </div>
                    {isIllustrative && capitalExplanation.illustrativeFacilityAfterIncrease != null ? (
                      <>
                        <div className="sm2-cap-amount">
                          {formatScenarioCurrency(capitalExplanation.illustrativeFacilityAfterIncrease, { style: 'compact' })}
                        </div>
                        <div className="sm2-cap-note">
                          Illustrative facility after 10% increase:{' '}
                          {formatScenarioCurrency(capitalExplanation.illustrativeFacilityAfterIncrease, { style: 'full' })}
                        </div>
                        <div className="sm2-cap-note">
                          Incremental increase:{' '}
                          {formatScenarioCurrency(capitalExplanation.illustrativeIncrementalIncrease, { style: 'full' })}
                        </div>
                        <div className="sm2-cap-note">
                          Remaining uncovered need:{' '}
                          {formatScenarioCurrency(capitalExplanation.remainingUncoveredNeed, { style: 'full' })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="sm2-cap-amount">{c.amount}</div>
                        <div className="sm2-cap-note">
                          {isPrimary ? capitalExplanation.explanation : c.note}
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="sm2-empty-note">
                No capital recommendations yet for this scenario. Recalculate to generate.
              </div>
            )}
          </div>
          {view?.capital?.length ? (
            <div className="sm2-card sm2-cap-reconcile" data-testid="capital-rec-reconcile" style={{ marginTop: 12 }}>
              <div className="sm2-card-header">
                <span className="sm2-card-title">Capital recommendation reconciliation</span>
              </div>
              <div className="sm2-lv-grid" data-testid="capital-rec-primary-rows">
                {(capitalExplanation.primaryRows || capitalExplanation.rows.filter((r) => r.priority !== 'secondary')).map((row) => (
                  <LabeledValueRow
                    key={row.key}
                    label={row.label}
                    labeled={{ value: row.value, source: row.source, note: row.note }}
                    format={(v) => formatScenarioCurrency(v, { style: 'full' })}
                  />
                ))}
              </div>
              {(capitalExplanation.secondaryRows || []).length > 0 ? (
                <div className="sm2-lv-grid" data-testid="capital-rec-secondary-rows" style={{ marginTop: 12, opacity: 0.95 }}>
                  <div className="sm2-proj-subhead" style={{ marginBottom: 6 }}>Secondary context</div>
                  {capitalExplanation.secondaryRows.map((row) => (
                    <LabeledValueRow
                      key={row.key}
                      label={row.label}
                      labeled={{ value: row.value, source: row.source, note: row.note }}
                      format={(v) => formatScenarioCurrency(v, { style: 'full' })}
                    />
                  ))}
                </div>
              ) : null}
              {capitalExplanation.remainingUncoveredNeed > 0 ? (
                <div
                  className="sm2-inline-error sm2-inline-error-amber"
                  data-testid="capital-illustrative-remainder"
                  style={{ marginTop: 10 }}
                >
                  <div className="sm2-inline-error-title">Illustrative 10% scenario does not fully fund</div>
                  <div>
                    Remaining uncovered need versus modeled target LOC facility:{' '}
                    {formatScenarioCurrency(capitalExplanation.remainingUncoveredNeed, { style: 'full' })}.
                    Primary figure remains the modeled target LOC facility.
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <RequestBondJourney
          orgDisplayName={ORG_DISPLAY_NAME}
          projectContext={projectContext}
          financialAsOfLabel={actuals?.asOfDate || null}
          disabled={requestBondBlocked}
          disabledReason={
            projectContextIssue
              ? projectContextIssue
              : resultsStale
                ? 'Recalculation required before Request Bond.'
                : !view || runStatus !== 'success'
                  ? 'Run a successful scenario calculation before Request Bond.'
                  : null
          }
          decisionSummary={decisionSummary}
          resultsStale={resultsStale}
        />

        {/* ── RISK FLAGS ──────────────────────────────────────────── */}
        <div>
          <div className="sm2-card-header" style={{ marginBottom: 10 }}>
            <span className="sm2-card-title">Risk Flags</span>
          </div>
          <div className="sm2-risk-flags">
            {view?.risks?.length ? (
              view.risks.map((r, i) => (
                <div key={i} className={`sm2-risk-flag sm2-risk-${r.level}`}>
                  <div className={`sm2-risk-dot sm2-dot-${r.level}`} />
                  <span>{formatBareDollarAmountsInText(r.text)}</span>
                </div>
              ))
            ) : (
              <div className="sm2-empty-note">
                Risk flags will appear here after the scenario is projected.
              </div>
            )}
          </div>
        </div>

        {/* ── UNDERWRITING PROFILE ────────────────────────────────── */}
        <div className="sm2-partner-hook" data-testid="partner-conversation-profile">
          <div className="sm2-card-header">
            <span className="sm2-card-title">Partner Conversation Profile</span>
          </div>

          <div className="sm2-context-block" data-testid="partner-this-project">
            <div className="sm2-proj-subhead">This project</div>
            {addedProjectEnabled && (addedProject?.projectName || view?.addedProjectImpact) ? (
              <div>
                <PhRow
                  k="Project"
                  v={addedProject?.projectName || view?.addedProjectImpact?.projectName || '—'}
                />
                <PhRow
                  k="Contract value"
                  v={
                    addedProject?.contractValue
                      ? `${formatScenarioCurrency(addedProject.contractValue, { style: 'full' })} (customer-entered)`
                      : '—'
                  }
                />
                <PhRow
                  k="Estimated bonding discussion amount"
                  v={
                    addedProject?.bondingRequirement
                      ? `${formatScenarioCurrency(addedProject.bondingRequirement, { style: 'full' })} (customer-entered — not a bondability determination)`
                      : '—'
                  }
                />
                <PhRow
                  k="Modeled working-capital impact"
                  v={
                    view?.addedProjectImpact?.workingCapitalImpact?.value != null
                      ? `${formatScenarioCurrency(view.addedProjectImpact.workingCapitalImpact.value, { style: 'full' })} (ConstructPay calculation)`
                      : '—'
                  }
                />
                <PhRow
                  k="Project risk / engagement"
                  v={view?.partner?.urgency || '—'}
                />
              </div>
            ) : (
              <div className="sm2-empty-note">
                Enable “Model this project&apos;s impact” and recalculate to isolate this project from portfolio context.
              </div>
            )}
          </div>

          <div className="sm2-context-block" data-testid="partner-portfolio-context" style={{ marginTop: 14 }}>
            <div className="sm2-proj-subhead">Broader company / pipeline context</div>
            <div className="sm2-ap-desc" style={{ marginBottom: 8 }}>
              Portfolio figures below are company-level context. They do not describe the modeled project above.
            </div>
            <PhRow k="Lending opportunity (portfolio)" v={view?.partner?.opportunity || '—'} />
            <PhRow k="Company risk profile" v={view?.partner?.risk || '—'} />
            <PhRow k="Pipeline projects" v={view?.partner?.pipeline || '—'} />
          </div>
        </div>

        {hasProjectionDetails && view ? (
          <details className="sm2-proj-details">
            <summary className="sm2-proj-details-summary">Projection Details</summary>
            <div className="sm2-proj-details-body">
              {view.outflowScheduleRows.length > 0 ? (
                <div className="sm2-proj-details-block">
                  <div className="sm2-proj-subhead">Outflow schedule</div>
                  <ProjectionDetailTable rows={view.outflowScheduleRows} />
                </div>
              ) : null}
              {view.arCollectionRows.length > 0 ? (
                <div className="sm2-proj-details-block">
                  <div className="sm2-proj-subhead">AR collections</div>
                  <ProjectionDetailTable rows={view.arCollectionRows} />
                </div>
              ) : null}
            </div>
          </details>
        ) : null}

        {/* ── FOOTER NOTE ─────────────────────────────────────────── */}
        <div className="sm2-model-note">
          Projections modeled from current-period actuals in CFO Insights. Capital recommendations are directional — consult your financial advisor and surety agent before acting.
        </div>
      </div>
    </CfoInsightsShell>
  );
}

/* ── Small presentational helpers ───────────────────────────────────────── */

function KpiCard({ label, value, signal, delta, deltaClass }) {
  return (
    <div className="sm2-mcard">
      <div className={`sm2-mcard-signal ${signal}`} />
      <div className="sm2-mcard-label">{label}</div>
      <div className="sm2-mcard-value">{value}</div>
      <div className={`sm2-mcard-delta ${deltaClass}`}>{delta}</div>
    </div>
  );
}

function AssumpRow({ label, value, baseline, onChange, onBlur, disabled }) {
  return (
    <div className="sm2-assump-row">
      <span className="sm2-assump-key">{label}</span>
      <input
        className="sm2-assump-input"
        type="text"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
      />
      <span className="sm2-assump-baseline">{baseline}</span>
    </div>
  );
}

function ActualRow({ k, v, source }) {
  return (
    <div className="sm2-actual-row">
      <span className="sm2-actual-key">
        {k}
        {source ? <SourceBadge source={source} /> : null}
      </span>
      <span className="sm2-actual-val">{v}</span>
    </div>
  );
}

function PhRow({ k, v }) {
  return (
    <div className="sm2-ph-row">
      <span className="sm2-ph-key">{k}</span>
      <span className="sm2-ph-val">{v}</span>
    </div>
  );
}

/* ── Source/assumption/projection/missing-data labeling ─────────────────── */
// Spec §11.D / §12: every output must distinguish source-backed actuals,
// customer-entered assumptions, ConstructPay-calculated projections, missing
// data, and policy-required metrics. Mirrors the backend's `source` field.

const SOURCE_BADGE_META = {
  source_backed: { label: 'Source-backed', className: 'sm2-badge-source' },
  customer_assumption: { label: 'Your assumption', className: 'sm2-badge-assumption' },
  constructpay_projection: { label: 'ConstructPay projection', className: 'sm2-badge-projection' },
  missing_data: { label: 'Missing data', className: 'sm2-badge-missing' },
  policy_required: { label: 'Policy review required', className: 'sm2-badge-policy' },
};

function SourceBadge({ source }) {
  const meta = SOURCE_BADGE_META[source];
  if (!meta) return null;
  return <span className={`sm2-badge ${meta.className}`}>{meta.label}</span>;
}

/** Renders a LabeledValue { value, source, note } as a labeled metric row. */
function LabeledValueRow({ label, labeled, format }) {
  if (!labeled) return null;
  const hasValue = labeled.value !== null && labeled.value !== undefined;
  return (
    <div className="sm2-lv-row">
      <div className="sm2-lv-top">
        <span className="sm2-lv-label">{label}</span>
        <SourceBadge source={labeled.source} />
      </div>
      <div className="sm2-lv-value">
        {hasValue ? (format ? format(labeled.value) : labeled.value) : '—'}
      </div>
      {labeled.note ? <div className="sm2-lv-note">{labeled.note}</div> : null}
    </div>
  );
}

/* ── Add a Project (spec §11.B / §13) ────────────────────────────────────── */

function AddProjectField({ label, children }) {
  return (
    <label className="sm2-ap-field">
      <span className="sm2-ap-field-label">{label}</span>
      {children}
    </label>
  );
}

function AddProjectPanel({ project, onChange, enabled, onToggle, disabled }) {
  const set = (key) => (e) => onChange({ ...project, [key]: e.target.value });

  return (
    <div className="sm2-card sm2-add-project">
      <div className="sm2-card-header">
        <span className="sm2-card-title">Add a Project</span>
        <label className="sm2-ap-toggle">
          <input
            type="checkbox"
            checked={enabled}
            disabled={disabled}
            onChange={(e) => onToggle(e.target.checked)}
          />
          Model this project's impact
        </label>
      </div>
      <div className="sm2-ap-desc">
        Model the impact of adding one specific project to your current baseline. Required fields
        are marked. Switching scenario tabs resets to that tab's defaults — click Recalculate to
        see this project's impact under Base, Growth, or Stress.
      </div>
      {enabled ? (
        <div className="sm2-ap-grid">
          <AddProjectField label="Project name *">
            <input type="text" value={project.projectName} onChange={set('projectName')} disabled={disabled} />
          </AddProjectField>
          <AddProjectField label="Project type">
            <input type="text" value={project.projectType} onChange={set('projectType')} disabled={disabled} placeholder="e.g. Commercial buildout" />
          </AddProjectField>
          <AddProjectField label="Contract value ($) *">
            <input type="number" value={project.contractValue} onChange={set('contractValue')} disabled={disabled} />
          </AddProjectField>
          <AddProjectField label="Estimated gross margin (%)">
            <input type="number" value={project.grossMarginEstimate} onChange={set('grossMarginEstimate')} disabled={disabled} />
          </AddProjectField>
          <AddProjectField label="Start date *">
            <input type="date" value={project.startDate} onChange={set('startDate')} disabled={disabled} />
          </AddProjectField>
          <AddProjectField label="End date">
            <input type="date" value={project.endDate} onChange={set('endDate')} disabled={disabled} />
          </AddProjectField>
          <AddProjectField label="Expected collection timing (days)">
            <input type="number" value={project.paymentTermsDays} onChange={set('paymentTermsDays')} disabled={disabled} placeholder="45" />
          </AddProjectField>
          <AddProjectField label="Retainage (%)">
            <input type="number" value={project.retainagePct} onChange={set('retainagePct')} disabled={disabled} placeholder="10" />
          </AddProjectField>
          <AddProjectField label="AP timing (days)">
            <input type="number" value={project.apTimingDays} onChange={set('apTimingDays')} disabled={disabled} placeholder="30" />
          </AddProjectField>
          <AddProjectField label="Material deposit / upfront purchase ($)">
            <input type="number" value={project.materialDepositAmount} onChange={set('materialDepositAmount')} disabled={disabled} placeholder="Optional" />
          </AddProjectField>
          <AddProjectField label="Estimated bonding requirement ($)">
            <input type="number" value={project.bondingRequirement} onChange={set('bondingRequirement')} disabled={disabled} placeholder="Optional" />
          </AddProjectField>
          <AddProjectField label="Estimated capital requirement ($)">
            <input type="number" value={project.capitalRequirement} onChange={set('capitalRequirement')} disabled={disabled} placeholder="Optional" />
          </AddProjectField>
          <AddProjectField label="Confidence (%)">
            <input type="number" value={project.confidencePct} onChange={set('confidencePct')} disabled={disabled} placeholder="Optional" />
          </AddProjectField>
        </div>
      ) : null}
    </div>
  );
}

function AddedProjectImpactCard({ impact }) {
  return (
    <div className="sm2-card sm2-added-impact">
      <div className="sm2-card-header">
        <span className="sm2-card-title">
          Project Impact{impact.projectName ? ` — ${impact.projectName}` : ''}
        </span>
      </div>
      <div className="sm2-lv-grid">
        <LabeledValueRow
          label={`Cash trough${impact.cashTroughMonth ? ` (${impact.cashTroughMonth})` : ''}`}
          labeled={impact.cashTrough}
          format={formatUsdMagnitude}
        />
        <LabeledValueRow label="Working capital impact" labeled={impact.workingCapitalImpact} format={formatUsdMagnitude} />
        <LabeledValueRow label="Capital need (calculated)" labeled={impact.capitalNeed} format={formatUsdMagnitude} />
        <LabeledValueRow label="Capital requirement entered" labeled={impact.capitalRequirementEntered} format={formatUsdMagnitude} />
        <LabeledValueRow label="AP pressure" labeled={impact.apPressure} format={formatUsdMagnitude} />
        <LabeledValueRow label="Retainage impact" labeled={impact.retainageImpact} format={formatUsdMagnitude} />
        <LabeledValueRow label="Bonding discussion amount (customer-entered)" labeled={impact.bondingDiscussionArea} format={formatUsdMagnitude} />
      </div>
      {impact.assumptionsUsed?.length ? (
        <div className="sm2-ap-assumptions">
          {impact.assumptionsUsed.map((a, i) => (
            <div key={i} className="sm2-ap-assumption-row">{a}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ProjectionDetailTable({ rows }) {
  if (!rows?.length) return null;
  const keySet = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((k) => keySet.add(k));
  });
  const keys = [...keySet].sort().slice(0, 12);
  if (!keys.length) return null;
  return (
    <div className="sm2-proj-table-wrap">
      <table className="sm2-proj-table">
        <thead>
          <tr>
            {keys.map((k) => (
              <th key={k}>{humanizeDetailKey(k)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {keys.map((k) => (
                <td key={k}>{formatDetailCell(row[k])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── KPI signal + delta helpers (map spec thresholds) ───────────────────── */

function signalFor(value, thresholds, direction = 'higher') {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return 'sm2-signal-amber';
  const [good, ok] = thresholds;
  if (direction === 'higher') {
    if (n >= good) return 'sm2-signal-green';
    if (n >= ok) return 'sm2-signal-amber';
    return 'sm2-signal-red';
  }
  if (n <= good) return 'sm2-signal-green';
  if (n <= ok) return 'sm2-signal-amber';
  return 'sm2-signal-red';
}

function deltaText(value, baseline, { unit = '', prefix = '', decimals = 1 } = {}) {
  if (value == null || baseline == null) return '—';
  const n = Number(value) - Number(baseline);
  const sign = n >= 0 ? '+' : '';
  return `${sign}${prefix}${n.toFixed(decimals)}${unit} vs base`;
}

function deltaClass(value, baseline) {
  if (value == null || baseline == null) return 'sm2-delta-flat';
  const diff = Number(value) - Number(baseline);
  if (diff > 0) return 'sm2-delta-up';
  if (diff < 0) return 'sm2-delta-down';
  return 'sm2-delta-flat';
}

function formatDcoh(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return String(Math.round(Number(n)));
}
